import { Scissors, Sparkles, Heart, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const WHATSAPP_NUMBER = "8898798857";

const iconMap = {
  Scissors: Scissors,
  Sparkles: Sparkles,
  Heart: Heart,
};

const servicesData = [
  {
    id: "hair-care",
    name: "Hair Care",
    description: "Expert hair services for men, women & kids",
    icon: "Scissors",
    services: [
      { name: "Men, Women & Kids Haircuts", description: "Precision cuts tailored to your face shape and style preferences" },
      { name: "Global Hair Colour", description: "Full head color transformation with premium products" },
      { name: "Balayage", description: "Hand-painted highlights for a natural, sun-kissed look" },
      { name: "Highlights", description: "Strategic color placement for dimension and depth" },
      { name: "Protein Treatments", description: "Restore strength and shine to damaged hair" },
      { name: "Hair Straightening", description: "Smooth, frizz-free hair with lasting results" },
      { name: "Hair Perming", description: "Beautiful waves and curls that last" },
      { name: "Hair Extensions", description: "Add length and volume instantly" }
    ]
  },
  {
    id: "skincare-spa",
    name: "Skincare & Spa",
    description: "Relaxing treatments for radiant skin",
    icon: "Sparkles",
    services: [
      { name: "Facials", description: "Deep cleansing and nourishing treatments for all skin types" },
      { name: "Hydra-Facials", description: "Advanced hydration therapy for glowing skin" },
      { name: "Advanced Skin Treatments", description: "Targeted solutions for specific skin concerns" },
      { name: "Spa Therapies", description: "Relaxing body treatments to rejuvenate mind and body" }
    ]
  },
  {
    id: "grooming-makeup",
    name: "Grooming & Makeup",
    description: "Complete beauty services for every occasion",
    icon: "Heart",
    services: [
      { name: "Bridal Makeup", description: "Look stunning on your special day with expert bridal styling" },
      { name: "Party Makeup", description: "Glamorous looks for any celebration" },
      { name: "Manicure", description: "Pamper your hands with our premium nail care" },
      { name: "Pedicure", description: "Complete foot care and nail treatments" },
      { name: "Fruit Waxing", description: "Gentle hair removal with natural fruit extracts" }
    ]
  }
];

export default function ServicesPage() {
  const services = servicesData;

  const handleBookService = (serviceName) => {
    const message = encodeURIComponent(`Hi, I'd like to book an appointment for ${serviceName} at Bunty Hair Master.`);
    window.open(`https://wa.me/91${WHATSAPP_NUMBER}?text=${message}`, "_blank");
  };

  const handleBookAppointment = () => {
    const message = encodeURIComponent("Hi, I'd like to book an appointment at Bunty Hair Master.");
    window.open(`https://wa.me/91${WHATSAPP_NUMBER}?text=${message}`, "_blank");
  };

  return (
    <div className="min-h-screen pt-20" data-testid="services-page">
      {/* Hero Section */}
      <section className="section-spacing bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="font-['Cormorant_Garamond'] text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-4">
              Our Services
            </p>
            <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl text-white font-semibold leading-tight mb-6">
              Premium Hair & Beauty Services
            </h1>
            <p className="text-white/70 text-lg">
              From expert haircuts to relaxing spa treatments, we offer a complete range of 
              services designed to make you look and feel your best.
            </p>
          </div>
        </div>
      </section>

      {/* Services List */}
      {services.map((category, catIndex) => {
          const IconComponent = iconMap[category.icon] || Scissors;
          return (
            <section 
              key={category.id}
              className={`section-spacing ${catIndex % 2 === 0 ? 'bg-[#121212]' : 'bg-[#0A0A0A]'}`}
              data-testid={`service-category-${category.id}`}
            >
              <div className="max-w-7xl mx-auto container-padding">
                {/* Category Header */}
                <div className="flex items-start gap-6 mb-12">
                  <div className="w-16 h-16 bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                    <IconComponent className="w-8 h-8 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h2 className="font-['Playfair_Display'] text-3xl text-white font-semibold mb-2">
                      {category.name}
                    </h2>
                    <p className="text-white/60">{category.description}</p>
                  </div>
                </div>

                {/* Services Grid */}
                <div className="grid md:grid-cols-2 gap-6">
                  {category.services.map((service, svcIndex) => (
                    <div 
                      key={svcIndex}
                      className="bg-[#1A1A1A] border border-white/5 p-6 hover:border-[#D4AF37]/30 transition-colors group"
                      data-testid={`service-item-${category.id}-${svcIndex}`}
                    >
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex-1">
                          <h3 className="font-['Playfair_Display'] text-xl text-white mb-2 group-hover:text-[#D4AF37] transition-colors">
                            {service.name}
                          </h3>
                          <p className="text-white/60 text-sm mb-4">
                            {service.description}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleBookService(service.name)}
                        className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#F3E5AB] transition-colors text-sm font-medium"
                        data-testid={`book-service-btn-${category.id}-${svcIndex}`}
                      >
                        Book Now
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          );
        })}

      {/* CTA Section */}
      <section className="section-spacing bg-[#121212] border-t border-white/5">
        <div className="max-w-4xl mx-auto container-padding text-center">
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-white font-semibold mb-6">
            Ready to Transform Your Look?
          </h2>
          <p className="text-white/60 mb-8 max-w-2xl mx-auto">
            Book your appointment today and let our expert team help you achieve the look you've always wanted.
          </p>
          <Button
            onClick={handleBookAppointment}
            className="bg-[#D4AF37] text-black hover:bg-[#B5952F] font-medium px-10 py-4 rounded-none text-lg shadow-[0_0_20px_rgba(212,175,55,0.3)] hover:shadow-[0_0_30px_rgba(212,175,55,0.5)]"
            data-testid="services-cta-book-btn"
          >
            Book Your Appointment
          </Button>
        </div>
      </section>
    </div>
  );
}
